/** @format */

import { API_URL, PASSPORT_FILE_DELETE } from "../../constants";
import { CircumstancesType } from "../constants";
import { showErrorModal } from "./ErrorHandling";
import { Storage, Store } from "./Storage";
import { parseArray } from "./passport.utils";
import { DELETE_API_URL, PASSPORT_FILE_API_URL } from "../../constants";

const Push = async (data: Store, user_id: string, accessToken: string) => {
	const circumstancesKey = "custom:circumstances";

	const circumstancesValue =
		((await Storage.getValue(circumstancesKey)) as string) ?? "";
	const circumstancesObjectMap = circumstancesValue
		.split(",")
		.filter((val) => !!val) as CircumstancesType[];

	const isNeurodiversityHidden = circumstancesObjectMap.includes(
		CircumstancesType.NEURODIVERSITY
	);
	const isMentalHealthHidden = circumstancesObjectMap.includes(
		CircumstancesType.MENTAL_HEALTH
	);
	const isWellbeingHidden = circumstancesObjectMap.includes(
		CircumstancesType.WELLBEING
	);
	const isDisabilityHidden = circumstancesObjectMap.includes(
		CircumstancesType.DISABILITY
	);

	const formData = new FormData();
	formData.append("user_id", user_id);
	formData.append("circumstances", circumstancesValue);
	formData.append("password", data.password);
	formData.append("given_name", data.given_name); //firstName
	formData.append("family_name", data.family_name); //lastName
	formData.append("email", data.email);
	formData.append("profile_nickname", data.profile_nickname);
	formData.append("profile_dob", data.profile_dob);
	formData.append("profile_gp_details", data.profile_gp_details);
	formData.append("profile_support_network", data.profile_support_network);
	formData.append("profile_support_services", data.profile_support_services);
	formData.append("profile_contact", data.profile_contacts);
	formData.append("custom:circumstances", data["custom:circumstances"]);
	formData.append("profile_university", data.profile_university);
	formData.append("profile_course", data.profile_course);
	formData.append("gender", data.gender);
	formData.append("profile_work ", data.profile_work);
	formData.append("passport_strategies", data.passport_strategies);
	formData.append("passport_network", data.passport_network);
	formData.append("passport_signs", data.passport_signs);
	formData.append("passport_triggers", data.passport_triggers);
	formData.append("passport_impact", data.passport_impact);
	formData.append("passport_aspects", data.passport_aspects);
	formData.append(
		"mh_issues_current",
		isMentalHealthHidden ? data.mh_issues_current : "N/A"
	);
	formData.append(
		"mh_issues_past",
		isMentalHealthHidden ? data.mh_issues_past : "N/A"
	);
	formData.append(
		"mh_challenges",
		isMentalHealthHidden ? data.mh_challenges : "N/A"
	);
	formData.append(
		"neuro_diagnosed",
		isNeurodiversityHidden ? data.neuro_diagnosed : "N/A"
	);
	formData.append(
		"neuro_details",
		isNeurodiversityHidden
			? JSON.stringify(parseArray(data.neuro_details))
			: "N/A"
	);
	formData.append(
		"neuro_challenges",
		isNeurodiversityHidden ? data.neuro_challenges : "N/A"
	);
	formData.append("wb_current", isWellbeingHidden ? data.wb_current : "N/A");
	formData.append("wb_past", isWellbeingHidden ? data.wb_past : "N/A");
	formData.append(
		"wb_challenges",
		isWellbeingHidden ? data.wb_challenges : "N/A"
	);
	formData.append(
		"disabilty_details",
		isDisabilityHidden ? data.disabilty_details : "N/A"
	);
	formData.append(
		"disabilty_difficulties",
		isDisabilityHidden ? data.disabilty_difficulties : "N/A"
	);
	formData.append(
		"disabilty_challenges",
		isDisabilityHidden ? data.disabilty_challenges : "N/A"
	);

	return fetch(API_URL, {
		method: "POST",
		headers: {
			'Authorization': 'Bearer ' + accessToken,
		},
		body: formData,
	})
		.then(async (res) => {
			if (res.ok) {
				const result = await res.text();
				return result;
			} else throw new Error("Network request failed");
		})
		.catch((error) => {
			showErrorModal("An error occurred: " + error.message);
			throw new Error(error.message);
		});
};

const Delete = async (userId: string, accessToken: string) => {
	const url = `${DELETE_API_URL}?user_id=${userId}`;
	return fetch(url, {
		method: "POST",
		headers: {
			'Authorization': 'Bearer ' + accessToken,
		},
	})
		.then(async (res) => {
			if (res.ok) {
				const result = await res.text();
				return result;
			} else {
				throw new Error("Network request failed");
			}
		})
		.catch((error) => {
			showErrorModal("An error occurred: " + error.message);
			throw new Error(error.message);
		});
};

const createPassportFile = async (data: FormData, accessToken: string) => {
	// Add your access token to the default headers
	return fetch(PASSPORT_FILE_API_URL, {
		method: "POST",
		headers: {
			'Authorization': 'Bearer ' + accessToken,
		},
		body: data,
	})
		.then(async (res) => {
			if (res.ok) {
				const result = await res.json();
				if (result.result === 1) {
					return result;
				} else {
					throw new Error(result.message);
				}
			} else throw new Error("Network request failed");
		})
		.catch((error) => {
			throw new Error(error.message);
		});
};

export const accessOrganization = async (
	url: string,
	email: string,
	version: string,
	accessToken: string
) => {
	const timestamp = new Date().valueOf();
	const newURL = `${url}?email=${email}&v=${version}&${timestamp}`;

	return fetch(newURL, {
		method: "GET",
		credentials: 'include',
		headers: {
			'Authorization': 'Bearer ' + accessToken,
            'Content-Type': 'application/json'
		},
	})
		.then(async (res) => {
			if (res.ok) {
				const result = await res.text();
				const parseResult = JSON.parse(result);
				if (parseResult.access === 0) {
					throw (
						{
							errorMessage: parseResult.errMsg,
							link: parseResult.link || null,
						})
				}
				return result;
			} else throw new Error("Network request failed");
		})
		.catch((error) => {
			throw error;
		});
};

export const deletePassportFile = async (userId: string, fileUrl: string, accessToken: string) => {
	const url = `${PASSPORT_FILE_DELETE}?user_id=${userId}&file_url=${fileUrl}`;
	return fetch(url, {
		method: "POST",
		headers: {
			'Authorization': 'Bearer ' + accessToken,
		},
	})
		.then(async (res) => {
			if (res.ok) {
				const result = await res.text();
				return result;
			} else throw new Error("Network request failed");
		})
		.catch((error) => {
			throw new Error(error.message);
		});
};

export const API = { Push, Delete, createPassportFile, deletePassportFile };
